import { type FC, useCallback, useEffect, useRef } from 'react';
import { Link, useBreakpointValue } from '@chakra-ui/react';
import { Mousewheel, Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useResizeObserver } from 'usehooks-ts';
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic';
import { BREAKPOINTS_PX } from '../../theme/foundations/breakpoints';
import { Text } from '../../ui-components/typography/text';
import { classNames } from '../../utils/tailwind/classNames';
import { GetIcon } from '../icons';
import styles from './CategoryImageTiles.module.scss';

export type Category = ShopperProductsTypes.Category & {
    c_productInfoTile?: string[];
    c_template?: string;
    c_href: string;
    c_slotBannerImage: string;
};

export type CategoryImageTilesProps = {
    items: Category[];
    dataTestId?: string;
};

/**
 * This component is used to render a list of categories as image tiles.
 *
 * @param calloutMsg - The message to display above the tiles.
 * @param items - The list of categories to render.
 * @returns A VStack with the callout message and the tiles.
 */
const CategoryImageTiles: FC<CategoryImageTilesProps> = ({ items, dataTestId }) => {
    const showNavigation = useBreakpointValue({ base: false, md: true });
    const containerRef = useRef<HTMLDivElement | null>(null);
    const firstImageRef = useRef<HTMLImageElement | null>(null);

    const updateArrowPosition = useCallback(() => {
        const container = containerRef.current;
        const firstImage = firstImageRef.current;

        if (!container || !showNavigation || !firstImage) {
            container?.style.removeProperty('--category-image-tiles-arrow-top');
            return;
        }

        const { height } = firstImage.getBoundingClientRect();
        const arrowHeight = 32;
        const computedTop = Math.max(height / 2 - arrowHeight / 2, 0);

        container.style.setProperty('--category-image-tiles-arrow-top', `${computedTop}px`);
    }, [showNavigation]);

    useResizeObserver({
        ref: firstImageRef,
        onResize: updateArrowPosition,
    });

    useEffect(() => {
        updateArrowPosition();
    }, [items, showNavigation, updateArrowPosition]);

    const handleFirstImageLoad = useCallback(() => {
        updateArrowPosition();
    }, [updateArrowPosition]);

    return (
        <div
            className={classNames(
                styles.categoryImageTiles,
                'relative h-auto w-full overflow-hidden',
            )}
            data-testid={dataTestId}
            ref={containerRef}
        >
            <Swiper
                data-testid={`${dataTestId}-category-image-tiles`}
                modules={[Scrollbar, Mousewheel, Navigation]}
                slidesPerView="auto"
                direction="horizontal"
                navigation={
                    showNavigation
                        ? {
                              nextEl: '.swiper-button-next',
                              prevEl: '.swiper-button-prev',
                          }
                        : false
                }
                scrollbar
                mousewheel
                // Using gap and spaceBetween at the same time to avoid flickering when the Swiper is loaded
                wrapperClass="gap-4 md:gap-6"
                breakpoints={{
                    [BREAKPOINTS_PX.base]: {
                        spaceBetween: 16,
                    },
                    [BREAKPOINTS_PX.md]: {
                        spaceBetween: 24,
                    },
                }}
            >
                {items.map((item: Category, index) => (
                    <SwiperSlide
                        data-testid={`${dataTestId}-category-image-tile-${item.id}`}
                        key={item.id}
                        className="!mr-0"
                    >
                        <Link
                            className="group flex h-auto min-h-[72px] w-full items-center gap-2 rounded-lg border border-slt-grey-light bg-slt-grey-light p-2 transition-all hover:border hover:border-slt-pepper-300 md:hidden"
                            href={item.c_href}
                            _hover={{ textDecoration: 'none' }}
                        >
                            {item.c_slotBannerImage && (
                                <img
                                    className="h-14 w-14 shrink-0 object-contain contrast-[90%]"
                                    src={item.c_slotBannerImage}
                                    alt={item.name}
                                />
                            )}
                            <Text
                                className="text-slt-pepper-1000 group-hover:underline"
                                size="small"
                                style="bold"
                            >
                                {item.name}
                            </Text>
                        </Link>
                        <Link
                            className="group hidden aspect-square flex-col items-center gap-2 md:flex"
                            key={item.id}
                            href={item.c_href}
                            _hover={{ textDecoration: 'none' }}
                        >
                            <figure className="flex h-[200px] w-[200px] flex-col gap-2 overflow-hidden">
                                <div
                                    ref={index === 0 ? firstImageRef : undefined}
                                    className="flex h-[128px] justify-center rounded-lg border border-slt-grey-light bg-slt-grey-light p-2 transition-colors group-hover:border group-hover:border-slt-pepper-300"
                                >
                                    <img
                                        className="aspect-square h-full object-contain contrast-[90%]"
                                        src={item.c_slotBannerImage}
                                        alt={item.name}
                                        onLoad={index === 0 ? handleFirstImageLoad : undefined}
                                    />
                                </div>
                                <Text
                                    className="flex flex-[1] group-hover:underline"
                                    size="small"
                                    style="bold"
                                    as="figcaption"
                                >
                                    {item.name}
                                </Text>
                            </figure>
                        </Link>
                    </SwiperSlide>
                ))}
            </Swiper>
            {showNavigation && (
                <>
                    <div className="swiper-button-next">
                        <GetIcon name="navigation-slide-arrow-right" size="md" />
                    </div>
                    <div className="swiper-button-prev">
                        <GetIcon name="navigation-slide-arrow-left" size="md" />
                    </div>
                </>
            )}
        </div>
    );
};

CategoryImageTiles.defaultProps = {
    dataTestId: undefined,
};

export { CategoryImageTiles };
